import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MarketingLayout } from "@/components/MarketingLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, CheckCircle2, FileCheck } from "lucide-react";

const helps = [
  "Explain eligibility in simple terms and check whether your case is likely to qualify",
  "Help gather and organise land records, bank details and PM-KISAN information",
  "Prepare your tractor requirement details — HP, brand, model, dealer quotation",
  "Guide you on loan / financing options if you need them",
  "Review your application for completeness before submission",
  "Support you through verification and status tracking",
];

const prepare = [
  "Personal details — name, father/husband name, date of birth, gender, category, Aadhaar-linked mobile, address",
  "Farm / land details — survey number, total & cultivable area, ownership type, land type, main crops, irrigation source, RTC/Pahani",
  "PM-KISAN beneficiary details — registration/beneficiary ID, e-KYC status, payment status",
  "Bank account details — for Direct Benefit Transfer (DBT)",
  "Tractor requirement — preferred HP, brand, model, dealer, quotation amount, own contribution, loan requirement",
];

const docs = [
  { name: "Aadhaar" }, { name: "RTC / Pahani (land record)" }, { name: "Bank Passbook / Cancelled Cheque" },
  { name: "PM-KISAN Beneficiary Details" }, { name: "Tractor Quotation" }, { name: "Passport-size Photograph" },
  { name: "FRUITS ID / Farmer ID", opt: true }, { name: "Caste Certificate", opt: true },
];

export default function TractorSubsidy() {
  const navigate = useNavigate();
  return (
    <MarketingLayout>
      <section className="hero-radial grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="text-sm text-slate-500 mb-4"><Link to="/" className="hover:text-emerald-700">Home</Link> / Tractor Subsidy</div>
          <h1 className="font-head font-extrabold text-3xl sm:text-4xl lg:text-5xl text-emerald-950 tracking-tight max-w-3xl">Tractor Subsidy Assistance</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">We guide farmers through preparing and submitting a tractor subsidy application — from eligibility basics to the final document checklist. Government schemes can cover up to ~50% of tractor cost.</p>
          <Button className="mt-7 rounded-full bg-brand hover:bg-emerald-800 text-white h-12 px-7" onClick={() => navigate("/login")} data-testid="subsidy-start-button">Start Application <ArrowRight className="ml-2 h-5 w-5" /></Button>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div>
            <h2 className="font-head font-bold text-2xl sm:text-3xl text-emerald-950">What this service covers</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Buying a tractor is one of the biggest investments a farming family makes. Government tractor subsidy schemes can reduce that cost, but the application process — eligibility checks, land records, PM-KISAN linkage, bank verification and document uploads — trips up many farmers. We help you get it right the first time.</p>
            <p className="mt-3 text-slate-600 leading-relaxed">Our role is <strong className="text-emerald-800">facilitation and preparation</strong>: we help you understand what's needed, organise your documents, and submit a clean, complete application to the concerned department. We do not approve subsidies and we do not guarantee approval — that decision belongs to the government.</p>
          </div>

          <div>
            <h2 className="font-head font-bold text-2xl sm:text-3xl text-emerald-950 mb-5">How we help you</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {helps.map((h) => (
                <div key={h} className="flex gap-3 bg-white border border-emerald-100 rounded-2xl p-4"><CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" /><span className="text-sm text-slate-600">{h}</span></div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-head font-bold text-2xl sm:text-3xl text-emerald-950 mb-5">Details we'll help you prepare</h2>
            <ul className="space-y-2.5">
              {prepare.map((p) => <li key={p} className="flex gap-3 text-slate-600"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" /><span className="text-sm leading-relaxed">{p}</span></li>)}
            </ul>
          </div>

          <div>
            <h2 className="font-head font-bold text-2xl sm:text-3xl text-emerald-950 mb-5">Documents to keep ready</h2>
            <Card className="rounded-2xl p-6 border-emerald-100 bg-[#f1f6f2]">
              <div className="grid sm:grid-cols-2 gap-3">
                {docs.map((d) => (
                  <div key={d.name} className="flex items-center gap-2.5 text-sm text-slate-700"><FileCheck className="h-4 w-4 text-emerald-600" /> {d.name} {d.opt && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-500">if applicable</span>}</div>
                ))}
              </div>
            </Card>
            <p className="mt-3 text-sm text-slate-500">Other documents may be required by the implementing department. Our team will guide you on anything specific to your case.</p>
          </div>

          <div className="rounded-3xl bg-emerald-900 p-10 text-center grain relative overflow-hidden">
            <h3 className="font-head font-extrabold text-white text-2xl">Start Your Tractor Subsidy Application</h3>
            <p className="mt-3 text-emerald-100/80">Log in with your mobile to begin. We'll walk you through every step.</p>
            <Button className="mt-6 rounded-full bg-white text-emerald-900 hover:bg-emerald-50 h-12 px-8 font-semibold" onClick={() => navigate("/login")}>Apply Now <ArrowRight className="ml-2 h-5 w-5" /></Button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
